export type RequestErrorKind =
  | 'network'
  | 'http'
  | 'graphql'
  | 'wsapi-auth-rejected'
  | 'session-expired';

export interface GraphQLErrorLike {
  message: string;
  extensions?: { code?: string };
}

export interface RequestErrorDetails {
  kind: RequestErrorKind;
  endpoint: string;
  url: string;
  status?: number;
  operation?: string;
  bodySnippet?: string;
  graphqlErrors?: GraphQLErrorLike[];
}

export interface RequestError {
  message: string;
  response?: { status?: number; errors?: GraphQLErrorLike[] };
  details: RequestErrorDetails;
}

const BODY_SNIPPET_LIMIT = 300;

export const parseOperationName = (document: string): string | undefined => {
  const match = document.match(/\b(?:query|mutation|subscription)\s+([A-Za-z_][A-Za-z0-9_]*)/);
  return match?.[1];
};

export const truncateBody = (body: string, limit = BODY_SNIPPET_LIMIT): string => {
  if (body.length <= limit) return body;
  return `${body.slice(0, limit)}… (${body.length - limit} more chars)`;
};

interface BuildArgs {
  kind: RequestErrorKind;
  endpoint: string;
  url: string;
  operation?: string;
  status?: number;
  bodySnippet?: string;
  graphqlErrors?: GraphQLErrorLike[];
  detail?: string;
}

const formatMessage = (args: BuildArgs): string => {
  const op = args.operation ? ` [${args.operation}]` : '';
  const at = ` at ${args.url}`;
  switch (args.kind) {
    case 'network':
      return `${args.endpoint}${op} unreachable${at} — ${args.detail ?? 'network error'}`;
    case 'http':
      return `${args.endpoint}${op} returned ${args.status ?? '?'}${at}${args.detail ? ` — ${args.detail}` : ''}`;
    case 'wsapi-auth-rejected':
      return `${args.endpoint}${op} rejected a freshly-issued token (${args.status ?? 401})${at}`;
    case 'session-expired':
      return args.detail ?? 'Your session has expired. Please sign in again.';
    case 'graphql':
      return args.graphqlErrors?.map((e) => e.message).join(', ') ?? 'GraphQL error';
  }
};

export const buildRequestError = (args: BuildArgs): RequestError => ({
  message: formatMessage(args),
  response: {
    status: args.status,
    errors: args.graphqlErrors,
  },
  details: {
    kind: args.kind,
    endpoint: args.endpoint,
    url: args.url,
    status: args.status,
    operation: args.operation,
    bodySnippet: args.bodySnippet,
    graphqlErrors: args.graphqlErrors,
  },
});
